/**
 * Google Maps Utility Functions
 * Shared helpers for converting Leaflet-based data to Google Maps format
 */

// ─── GeoJSON → Google Maps Paths ────────────────────────────────────────────

/**
 * Converts a GeoJSON geometry (Polygon or MultiPolygon) into an array of
 * Google Maps LatLng paths suitable for <Polygon paths={...} />.
 */
export const geoJsonToGooglePaths = (geoJson) => {
  if (!geoJson) return [];

  const geometry = geoJson.geometry || geoJson;

  if (geometry.type === "Polygon") {
    return geometry.coordinates.map((ring) =>
      ring.map(([lng, lat]) => ({ lat, lng }))
    );
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) =>
      polygon.map((ring) => ring.map(([lng, lat]) => ({ lat, lng })))
    );
  }

  // Point / LineString fallback
  if (geometry.type === "Point") {
    const [lng, lat] = geometry.coordinates;
    return [[{ lat, lng }]];
  }

  return [];
};

/**
 * Simple average-of-vertices centroid for a single Google Maps polygon ring
 * (array of {lat, lng}) — good enough for placing a name label inside the
 * shape, not a true geometric centroid.
 */
export const getPathCentroid = (path) => {
  if (!path || path.length === 0) return null;
  const sum = path.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / path.length, lng: sum.lng / path.length };
};

/**
 * Compute a google.maps.LatLngBounds from an array of paths
 */
export const getBoundsFromPaths = (paths) => {
  if (!window.google) return null;
  const bounds = new window.google.maps.LatLngBounds();
  paths.forEach((ring) => {
    ring.forEach((point) => {
      bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
    });
  });
  return bounds;
};

/**
 * Compute bounds from a GeoJSON object
 */
export const getBoundsFromGeoJson = (geoJson) => {
  const paths = geoJsonToGooglePaths(geoJson);
  return getBoundsFromPaths(paths);
};

/**
 * Compute combined bounds from multiple GeoJSON objects
 */
export const getCombinedBounds = (geoJsonArray) => {
  if (!window.google || !geoJsonArray || geoJsonArray.length === 0) return null;
  const bounds = new window.google.maps.LatLngBounds();
  geoJsonArray.forEach((geoJson) => {
    const paths = geoJsonToGooglePaths(geoJson);
    paths.forEach((ring) => {
      ring.forEach((point) => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
    });
  });
  return bounds;
};

// ─── Distance Calculation ───────────────────────────────────────────────────

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// ─── Point in Polygon (turf replacement for circles) ────────────────────────

export const isPointInCircle = (lat, lng, centerLat, centerLng, radiusKm) => {
  return calculateDistance(centerLat, centerLng, lat, lng) <= radiusKm;
};

// ─── Date Formatting ────────────────────────────────────────────────────────

export const formatIncidentDateTime = (dateTimeString) => {
  if (!dateTimeString) return "NA";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "NA";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${dd}-${mm}-${yyyy} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  } catch {
    return "NA";
  }
};

// ─── Facility type correction ───────────────────────────────────────────────

/**
 * Some facilities come back from the API tagged with the wrong facility_type
 * (e.g. a "Community Health Centre" mis-tagged as "Government Office Buliding",
 * or a "Relief Center" mis-tagged as "Community Kitchen"). Recognize known
 * name patterns and correct the type so the marker icon, popup, and Legend
 * all classify the facility consistently.
 */
export const normalizeFacilityValue = (facility) => {
  const rawType = facility?.value || "DEFAULT";
  const name = (facility?.text || facility?.name || facility?.facility_name || "").toLowerCase();

  if (/hospital|\bphc\b|\bhc\b|health\s*centre|health\s*center/.test(name)) {
    return "Hospital / PHC / HC";
  }
  if (/integrated\s*relief/.test(name)) {
    return "Integrated Relief Camp";
  }
  if (/relief\s*(camp|cent(?:er|re))/.test(name)) {
    return "Flood Relief Camp";
  }
  return rawType;
};

// ─── Icon Configs ───────────────────────────────────────────────────────────

export const facilityIconConfigs = {
  "Panchayat Bhawan": { color: "#3b82f6", emoji: "🏛️", bgColor: "#dbeafe", label: "Panchayat Bhawan" },
  "Flood Relief Camp": { color: "#06b6d4", emoji: "⛺", bgColor: "#cffafe", label: "Flood Relief Camp" },
  "Animal Shelter": { color: "#84cc16", emoji: "🐾", bgColor: "#ecfccb", label: "Animal Shelter" },
  "Goverment School": { color: "#8b5cf6", emoji: "🏫", bgColor: "#ede9fe", label: "Government School" },
  "Community Kitchen": { color: "#f59e0b", emoji: "🍲", bgColor: "#fef3c7", label: "Community Kitchen" },
  "Government Office Buliding": { color: "#64748b", emoji: "🏢", bgColor: "#f1f5f9", label: "Govt Office" },
  Helipad: { color: "#dc2626", emoji: "🚁", bgColor: "#fee2e2", label: "Helipad" },
  "Hospital / PHC / HC": { color: "#ef4444", emoji: "🏥", bgColor: "#fecaca", label: "Hospital/PHC" },
  "ITBP Camp": { color: "#0ea5e9", emoji: "🛡️", bgColor: "#e0f2fe", label: "ITBP Camp" },
  "Integrated Relief Camp": { color: "#10b981", emoji: "🏕️", bgColor: "#d1fae5", label: "Integrated Relief Camp" },
  DEFAULT: { color: "#6b7280", emoji: "📍", bgColor: "#f3f4f6", label: "Facility" },
};

export const supportPointIconConfigs = {
  ShelterHome: { color: "#3b82f6", emoji: "🏠", bgColor: "#eff6ff", label: "Shelter Home" },
  BlanketDistribution: { color: "#8b5cf6", emoji: "🧣", bgColor: "#f5f3ff", label: "Blanket Distribution" },
  MedicalCamp: { color: "#10b981", emoji: "⛺", bgColor: "#f0fdf4", label: "Medical Camp" },
  Hospital: { color: "#2563eb", emoji: "🏥", bgColor: "#eff6ff", label: "Hospital" },
  CollapsedBuildingZone: { color: "#dc2626", emoji: "🏚️", bgColor: "#fef2f2", label: "Collapsed Building Zone" },
  TemporaryShelter: { color: "#f59e0b", emoji: "⛺", bgColor: "#fffbeb", label: "Temporary Shelter" },
  RescueTeam: { color: "#059669", emoji: "🚐", bgColor: "#ecfdf5", label: "Rescue Team" },
  BlockedRoad: { color: "#ef4444", emoji: "🚧", bgColor: "#fee2e2", label: "Blocked Road" },
  FloodedZone: { color: "#06b6d4", emoji: "🏞️", bgColor: "#ecfeff", label: "Flooded Zone" },
  ReliefCamp: { color: "#14b8a6", emoji: "⛺", bgColor: "#f0fdfa", label: "Relief Camp" },
  RoadClosure: { color: "#dc2626", emoji: "⛔", bgColor: "#fef2f2", label: "Road Closure" },
  Helipad: { color: "#dc2626", emoji: "🚁", bgColor: "#fee2e2", label: "Helipad" },
  DEFAULT: { color: "#6b7280", emoji: "📍", bgColor: "#f9fafb", label: "Support Point" },
};

export const disasterIconConfigs = {
  FLOOD: { color: "#3B82F6", icon: "💧" },
  Flood: { color: "#3B82F6", icon: "💧" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️" },
  "Cold Wave": { color: "#6B7280", icon: "❄️" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍" },
  Earthquake: { color: "#8B5CF6", icon: "🌍" },
  Fire: { color: "#DC2626", icon: "🔥" },
  Cyclone: { color: "#F59E0B", icon: "🌀" },
  "Heat Wave": { color: "#F97316", icon: "🔥" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥" },
  AVALANCHE: { color: "#9333EA", icon: "🏔️" },
  Avalanche: { color: "#9333EA", icon: "🏔️" },
  "Cloud Burst": { color: "#0284C7", icon: "⛈️" },
  CLOUD_BURST: { color: "#0284C7", icon: "⛈️" },
  DEFAULT: { color: "#6B7280", icon: "⚠️" },
};

// ─── Popup HTML builders ────────────────────────────────────────────────────

export const buildFacilityPopupHtml = (facility, facilityDetails) => {
  const config = facilityIconConfigs[normalizeFacilityValue(facility)] || facilityIconConfigs["DEFAULT"];

  return `
    <div style="font-family: 'Segoe UI', Arial, sans-serif; min-width: 240px; max-width: 280px;">
      <div style="display: flex; align-items: flex-start; gap: 8px; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 2px solid ${config.color};">
        <div style="width: 36px; height: 36px; border-radius: 8px; background: ${config.bgColor}; border: 2px solid ${config.color}; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;">${config.emoji}</div>
        <div style="flex: 1;">
          <div style="font-size: 13px; font-weight: 700; color: #1f2937; margin-bottom: 4px; line-height: 1.2;">${facility.text || facility.name}</div>
          <span style="display: inline-block; background: ${config.bgColor}; color: ${config.color}; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;">${config.label}</span>
        </div>
      </div>
      ${
        facilityDetails
          ? `
        ${
          facilityDetails.Facilities?.map(
            (f) => `
          <div style="background: #eff6ff; border-radius: 6px; padding: 6px 10px; margin-bottom: 6px;">
            ${f["Facility Name"] ? `<div style="font-size: 9px; color: #1e40af; margin-bottom: 2px;">Facility Name</div><div style="font-size: 11px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">${f["Facility Name"]}</div>` : ""}
            ${f["Facility Address"] ? `<div style="font-size: 9px; color: #1e40af; margin-bottom: 2px;">Address</div><div style="font-size: 10px; color: #374151;">${f["Facility Address"]}</div>` : ""}
          </div>
        `
          ).join("") || ""
        }
        ${
          facilityDetails.Contacts?.length > 0
            ? `
          <div style="background: #fef3c7; border-radius: 6px; padding: 6px 10px; margin-bottom: 6px;">
            <div style="font-size: 9px; color: #92400e; margin-bottom: 4px; font-weight: 700;">👤 Contact Information</div>
            ${facilityDetails.Contacts.map(
              (contact) => `
              <div style="margin-bottom: 3px;">
                ${contact["Contact Name"] ? `<div style="font-size: 11px; font-weight: 600; color: #1f2937;">${contact["Contact Name"]}</div>` : ""}
                ${contact["Job Title"] ? `<div style="font-size: 10px; color: #6b7280;">${contact["Job Title"]}</div>` : ""}
                ${contact["Email"] ? `<div style="font-size: 10px; color: #374151;">✉️ ${contact["Email"]}</div>` : ""}
                ${contact["Phone Number"] ? `<div style="font-size: 10px; color: #374151;">📞 ${contact["Phone Number"]}</div>` : ""}
              </div>
            `
            ).join("")}
          </div>
        `
            : ""
        }
        ${
          facilityDetails.Resources?.length > 0
            ? `
          <div style="background: #d1fae5; border-radius: 6px; padding: 6px 10px;">
            <div style="font-size: 9px; color: #065f46; margin-bottom: 4px; font-weight: 700;">📦 Available Resources</div>
            ${facilityDetails.Resources.map(
              (resource) => `
              <div style="margin-bottom: 4px; padding: 4px; background: white; border-radius: 4px;">
                ${resource["Resource"] ? `<div style="font-size: 11px; font-weight: 600; color: #1f2937;">${resource["Resource"]}</div>` : ""}
                ${resource["Category"] ? `<div style="font-size: 9px; color: #6b7280;">${resource["Category"]}</div>` : ""}
                ${resource["Total"] ? `<div style="font-size: 10px; color: #10b981; font-weight: 600;">Available Qty: ${resource["Total"]}</div>` : ""}
              </div>
            `
            ).join("")}
          </div>
        `
            : ""
        }
      `
          : ""
      }
    </div>
  `;
};

export const buildSupportPointPopupHtml = (point) => {
  const config = supportPointIconConfigs[point.layerType] || supportPointIconConfigs["DEFAULT"];
  let extraFields = "";

  try {
    const extra = JSON.parse(point.extraInfo);
    Object.entries(extra)
      .filter(([key]) => key.toLowerCase() !== "reason")
      .forEach(([key, value]) => {
        const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
        extraFields += `
        <div style="background: #fffbeb; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
          <div style="font-size: 11px; color: #92400e; margin-bottom: 2px;">${label}</div>
          <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${value}</div>
        </div>
      `;
      });
  } catch {}

  return `
    <div style="font-family: 'Segoe UI', Arial, sans-serif; min-width: 250px; max-width: 310px;">
      <div style="display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #f3f4f6;">
        <div style="width: 44px; height: 44px; border-radius: 10px; background: ${config.bgColor}; border: 2px solid ${config.color}; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0;">${config.emoji}</div>
        <div style="flex: 1;">
          <div style="font-size: 15px; font-weight: 700; color: #1f2937; margin-bottom: 5px; line-height: 1.3;">${point.name}</div>
          <span style="display: inline-block; background: ${config.bgColor}; color: ${config.color}; padding: 3px 10px; border-radius: 12px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">${config.label}</span>
        </div>
      </div>
      <div style="background: #fff7ed; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
        <div style="font-size: 11px; color: #9a3412; margin-bottom: 2px;"><span style="margin-right: 4px;">⚠️</span> Disaster Type</div>
        <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${point.disasterType}</div>
      </div>
      <div style="background: #fefce8; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
        <div style="font-size: 11px; color: #854d0e; margin-bottom: 2px;"><span style="margin-right: 4px;">📍</span> Location</div>
        <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${point.district}, ${point.state}</div>
      </div>
      ${extraFields}
    </div>
  `;
};